import { useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect, useId, type KeyboardEvent } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { initials, cn } from '@/lib/utils';

export const UserMenu = () => {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    // Close on pointerdown outside the menu (fires before focus changes).
    const handlePointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Close when focus moves outside the menu (keyboard navigation).
    const handleFocusIn = (e: FocusEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [open]);

  // Roving tabindex: arrow keys move focus between menu items.
  const handleMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % menuItemCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + menuItemCount) % menuItemCount);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(menuItemCount - 1);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (!user) return null;

  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;

  const close = () => setOpen(false);

  // Number of interactive menu items (used for roving tabindex).
  const menuItemCount = 2;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[44px] items-center gap-2 rounded-md border border-border-base bg-surface-base px-2.5 py-1.5 text-sm transition-colors hover:bg-surface-muted active:bg-surface-subtle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-burgundy-600 text-xs font-semibold text-fg-inverse">
          {initials(displayName)}
        </div>
        <span className="hidden text-fg-base sm:inline">{displayName}</span>
        <ChevronDown className="h-3.5 w-3.5 text-fg-muted" />
      </button>

      {open && (
        <div
          id={menuId}
          className="absolute right-0 z-50 mt-2 w-56 origin-top-right overflow-hidden rounded-md border border-border-base bg-surface-base shadow-lg animate-slide-down"
          role="menu"
          aria-orientation="vertical"
          onKeyDown={handleMenuKeyDown}
        >
          <div className="border-b border-border-subtle px-3 py-2.5">
            <p className="text-sm font-medium text-fg-base">{displayName}</p>
            <p className="truncate text-xs text-fg-muted">{user.email}</p>
          </div>
          <div className="p-1">
            <button
              type="button"
              role="menuitem"
              tabIndex={activeIndex === 0 ? 0 : -1}
              ref={(el) => {
                if (el && activeIndex === 0) el.focus();
              }}
              className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm text-fg-base transition-colors hover:bg-surface-muted focus:bg-surface-muted focus:outline-none"
              onClick={() => {
                close();
                navigate('/settings/account');
              }}
            >
              <UserIcon className="h-4 w-4 text-fg-muted" />
              Mon profil
            </button>
            <div className="my-1 h-px bg-border-subtle" />
            <button
              type="button"
              role="menuitem"
              tabIndex={activeIndex === 1 ? 0 : -1}
              ref={(el) => {
                if (el && activeIndex === 1) el.focus();
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm transition-colors',
                'text-danger-600 hover:bg-danger-50 focus:bg-danger-50 focus:outline-none'
              )}
              onClick={async () => {
                close();
                await logout();
                navigate('/login', { replace: true });
              }}
            >
              <LogOut className="h-4 w-4" />
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
